/**
 * VIB-994: modus-lås for `upsells.billing_mode`.
 *
 * Erik-avklaring 20.07 (fire avklaringer, pkt. 4): `billing_mode` kan endres
 * FRITT mens mersalget er tomt, men LÅSES permanent så snart det har enten
 * (a) timeføring logget mot en av mersalgets budsjettlinjer, eller (b) en
 * faktura knyttet til seg (T&M: `invoices.upsell_id`; fastpris-arven:
 * `upsells.added_to_invoice_id`). Vernet håndheves i mutation-laget
 * (`use-upsell-mutations.ts`, live DB-telling) — denne fila er den rene,
 * testbare beslutningslogikken + en variant som avleder samme tilstand fra
 * data som allerede er lastet i `ProjectDetailContext` (til UI-disabling uten
 * ekstra rundtrip).
 */

export interface BillingModeLockState {
  hasTimeEntries: boolean;
  hasInvoice: boolean;
}

/** Ren beslutning: låst hvis EN av de to betingelsene er sanne. */
export function isBillingModeLocked(state: BillingModeLockState): boolean {
  return state.hasTimeEntries || state.hasInvoice;
}

export interface BillingModeLockCounts {
  timeEntryCount: number;
  invoiceCount: number;
  hasLegacyInvoiceLink: boolean;
}

/** Konverter live-tellinger (mutation-laget) til lock-state. */
export function lockStateFromCounts(counts: BillingModeLockCounts): BillingModeLockState {
  return {
    hasTimeEntries: counts.timeEntryCount > 0,
    hasInvoice: counts.invoiceCount > 0 || counts.hasLegacyInvoiceLink,
  };
}

export interface BudgetLineForLock {
  id: string;
  upsell_id: string | null;
}

export interface TimeEntryForLock {
  budget_line_id: string | null;
}

export interface InvoiceForLock {
  upsell_id?: string | null;
}

/**
 * Avled lock-state fra data som allerede er lastet i ProjectDetailContext
 * (budgetLines, timeEntries, invoices) — ingen ekstra nettverkskall. Brukes
 * KUN til UI-disabling/hint; mutation-laget er den autoritative sjekken (kan
 * avvike hvis konteksten er stale — mutationen feiler da med en tydelig feil).
 */
export function computeBillingModeLockFromContext(input: {
  upsellId: string;
  addedToInvoiceId: string | null;
  budgetLines: BudgetLineForLock[];
  timeEntries: TimeEntryForLock[];
  invoices: InvoiceForLock[];
}): BillingModeLockState {
  const lineIds = new Set(
    input.budgetLines.filter((l) => l.upsell_id === input.upsellId).map((l) => l.id),
  );
  const hasTimeEntries = input.timeEntries.some(
    (t) => t.budget_line_id != null && lineIds.has(t.budget_line_id),
  );
  const hasInvoice =
    !!input.addedToInvoiceId || input.invoices.some((i) => i.upsell_id === input.upsellId);
  return { hasTimeEntries, hasInvoice };
}
